import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Mail, CheckCircle2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { AuthLayout } from "../components/layout/AuthLayout";
import { API } from "../utils/constants";

/**
 * Page de réinitialisation du mot de passe — flux sécurisé en 2 étapes.
 *
 * - Sans paramètre `?token` dans l'URL → affiche le formulaire "Mot de passe oublié"
 *   (demande l'email, envoie un lien par mail).
 * - Avec `?token=XXX` dans l'URL (clic depuis l'email) → affiche le formulaire
 *   de choix du nouveau mot de passe, confirme la validité du token et met à jour.
 */
export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  // Mode "forgot" (demande du lien) — par défaut
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  // Mode "reset" (consomme le token)
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);

  const [loading, setLoading] = useState(false);

  const mode = token ? "reset" : "forgot";

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Email envoyé ! Vérifie ta boîte de réception.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error("Trop de demandes. Réessaie dans une heure.");
      } else {
        // Toujours afficher le succès (anti-énumération côté UX aussi)
        setSent(true);
      }
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success("Mot de passe réinitialisé !");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Lien invalide ou expiré.");
    }
    setLoading(false);
  };

  // ===== Écran de succès après reset =====
  if (success) {
    return (
      <AuthLayout>
        <div className="animate-slide-up text-center" data-testid="reset-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="text-emerald-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#210875" }}>
            Mot de passe modifié !
          </h2>
          <p className="text-muted-foreground mb-6">
            Tu peux maintenant te connecter avec ton nouveau mot de passe.
          </p>
          <div className="animate-spin w-6 h-6 border-4 border-primary border-t-transparent rounded-full mx-auto" />
        </div>
      </AuthLayout>
    );
  }

  // ===== Écran "email envoyé" =====
  if (sent) {
    return (
      <AuthLayout>
        <div className="animate-slide-up text-center" data-testid="forgot-sent">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="text-blue-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#210875" }}>
            Vérifie ta boîte mail
          </h2>
          <p className="text-muted-foreground mb-6">
            Si l'adresse <strong>{email}</strong> correspond à un compte Biz360, tu vas recevoir un
            email avec un lien de réinitialisation. Il est valable <strong>1 heure</strong>.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Pas reçu ? Vérifie tes courriers indésirables, puis
            <button
              type="button"
              onClick={() => setSent(false)}
              className="text-primary underline ml-1"
            >
              recommence
            </button>
            .
          </p>
          <button
            onClick={() => navigate("/login")}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} /> Retour à la connexion
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ===== Mode RESET (token présent dans l'URL) =====
  if (mode === "reset") {
    return (
      <AuthLayout>
        <div className="animate-slide-up" data-testid="reset-password-form">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="text-primary" size={28} />
          </div>
          <h2 className="text-3xl font-bold mb-2 text-center" style={{ color: "#210875" }}>
            Nouveau mot de passe
          </h2>
          <p className="text-muted-foreground mb-8 text-center">
            Choisis un mot de passe solide pour ton compte Biz360.
          </p>

          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nouveau mot de passe</label>
              <input
                type="password"
                data-testid="reset-new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full h-11 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                minLength={8}
                autoComplete="new-password"
                required
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">8 caractères minimum</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                data-testid="reset-confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full h-11 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                minLength={8}
                autoComplete="new-password"
                required
              />
            </div>

            <button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="w-full h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Enregistrement..." : "Réinitialiser mon mot de passe"}
            </button>
          </form>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Lien valable 1 heure, à usage unique.
          </p>
        </div>
      </AuthLayout>
    );
  }

  // ===== Mode FORGOT (par défaut, demande d'email) =====
  return (
    <AuthLayout>
      <div className="animate-slide-up" data-testid="forgot-password-form">
        <button
          onClick={() => navigate("/login")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft size={18} /> Retour à la connexion
        </button>

        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <KeyRound className="text-primary" size={28} />
        </div>
        <h2 className="text-3xl font-bold mb-2" style={{ color: "#210875" }}>
          Mot de passe oublié ?
        </h2>
        <p className="text-muted-foreground mb-8">
          Entre ton email : on t'envoie un lien pour choisir un nouveau mot de passe (valable 1h).
        </p>

        <form onSubmit={handleForgot} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              data-testid="forgot-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="email@exemple.com"
              autoComplete="email"
              required
              autoFocus
            />
          </div>

          <button
            type="submit"
            data-testid="forgot-submit"
            disabled={loading}
            className="w-full h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? "Envoi..." : "Recevoir le lien de réinitialisation"}
          </button>
        </form>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-900 flex items-start gap-2">
            <ShieldCheck size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              <strong>Sécurité :</strong> le lien envoyé est unique, chiffré et valable 1 heure.
              Nous ne stockons jamais ton nouveau mot de passe en clair.
            </span>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};
